import { useCallback, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@cbhq/cds-web/controls/Checkbox';
import { HStack, VStack } from '@cbhq/cds-web/layout';
import { PopoverPanel } from '@cbhq/cds-web/overlays';
import { Pressable } from '@cbhq/cds-web/system/Pressable';
import { Text } from '@cbhq/cds-web/typography/Text';

import type { MemberRole, MemberRoleMap } from '../../utils/constants';

const messages = defineMessages({
  popoverAccessibilityLabel: {
    id: 'Team.inviteTray.productRolePopover.popoverAccessibilityLabel',
    defaultMessage: 'Change roles for {productLabel}',
    description: 'Accessibility label for the product role popover panel',
  },
  triggerAccessibilityLabel: {
    id: 'Team.inviteTray.productRolePopover.triggerAccessibilityLabel',
    defaultMessage: '{roleLabel}, roles for {productLabel}, click to change',
    description: 'Accessibility label for the popover trigger pressable',
  },
});

export type ProductRolePopoverParams = {
  /**
   * All roles for the product. Assignable roles are toggleable; unassignable held
   * roles (`assignable === false`) render checked + disabled. Sorted
   * assignable-first here, so input order doesn't matter.
   */
  roleOptions: MemberRoleMap[];
  selectedRoleValues: MemberRole[];
  onRoleToggle: (value: MemberRole, isSelected: boolean) => void;
  roleLabel: string;
  productLabel: string;
  disabled?: boolean;
};

const CONTENT_POSITION = { placement: 'bottom-end', gap: 1 } as const;
const PANEL_WIDTH = 448;
const TRIGGER_TEXT_BASE = {
  textDecorationLine: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: '4px',
} as const;
const TRIGGER_TEXT_ACTIVE = { ...TRIGGER_TEXT_BASE, opacity: 1 } as const;
const TRIGGER_TEXT_INACTIVE = { ...TRIGGER_TEXT_BASE, opacity: 0.6 } as const;

type RoleCheckboxItemParams = {
  role: MemberRoleMap;
  // checked = member holds the role; disabled = not assignable (locked).
  checked: boolean;
  onToggle: (value: MemberRole, isSelected: boolean) => void;
  disabled?: boolean;
};

function RoleCheckboxItem({ role, checked, onToggle, disabled = false }: RoleCheckboxItemParams) {
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) {
        return;
      }
      onToggle(role.value, event.target.checked);
    },
    [disabled, onToggle, role.value],
  );

  return (
    <HStack as="label" width="100%" alignItems="center" gap={2} paddingX={3} paddingY={1}>
      <Checkbox
        value={role.value}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        controlSize={22}
        borderRadius={0}
        background={checked ? 'fg' : undefined}
        borderColor={checked ? 'fg' : undefined}
      />
      <VStack gap={0} alignItems="flex-start" flexGrow={1}>
        <Text as="span" font="label1" color={disabled ? 'fgMuted' : 'fg'} noWrap>
          {role.name}
        </Text>
        {role.description ? (
          <Text as="span" font="label2" color="fgMuted">
            {role.description}
          </Text>
        ) : null}
      </VStack>
    </HStack>
  );
}

export function ProductRolePopover({
  roleOptions,
  selectedRoleValues,
  onRoleToggle,
  roleLabel,
  productLabel,
  disabled,
}: ProductRolePopoverParams) {
  const { formatMessage } = useIntl();

  // Assignable roles first, unassignable (held) roles last.
  const orderedRoleOptions = useMemo(
    () =>
      [...roleOptions].sort(
        (a, b) => Number(a.assignable === false) - Number(b.assignable === false),
      ),
    [roleOptions],
  );

  const renderContent = useCallback(
    () => (
      <VStack gap={0} width="100%" paddingY={1}>
        {orderedRoleOptions.map((role) => (
          <RoleCheckboxItem
            key={role.value}
            role={role}
            checked={selectedRoleValues.includes(role.value)}
            onToggle={onRoleToggle}
            disabled={!role.assignable}
          />
        ))}
      </VStack>
    ),
    [onRoleToggle, orderedRoleOptions, selectedRoleValues],
  );

  return (
    <PopoverPanel
      panelWidth={PANEL_WIDTH}
      maxPanelWidth={PANEL_WIDTH}
      disabled={disabled}
      accessibilityLabel={formatMessage(messages.popoverAccessibilityLabel, { productLabel })}
      contentPosition={CONTENT_POSITION}
      content={renderContent}
    >
      <Pressable
        background="transparent"
        noScaleOnPress
        disabled={disabled}
        accessibilityLabel={formatMessage(messages.triggerAccessibilityLabel, {
          productLabel,
          roleLabel,
        })}
      >
        <Text
          as="span"
          font="label2"
          color="fgMuted"
          textAlign="end"
          style={selectedRoleValues.length > 0 ? TRIGGER_TEXT_ACTIVE : TRIGGER_TEXT_INACTIVE}
        >
          {roleLabel}
        </Text>
      </Pressable>
    </PopoverPanel>
  );
}
